import * as XLSX from 'xlsx';

import { FileDeletionType, UploadFailureReason } from '../../../backend/src/types/types';
import { UserV2 } from '../../../backend/src/user/user.entity';
import { FileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { useLazyUserMetadata } from '@/api/queries/useGetUserMetadata';
import type { File, SkippedFile, DeletionHistory, NotProcessedDoc, ProcessedDoc } from '@/api/types/backend-dto-types';
import { UploadFailure } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';

/**
 * Downloads data as a CSV file
 */
export function downloadCsv(headers: string[], rows: string[][], filename: string): void {
  const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get human-readable label for upload failure reason
 */
export function getRejectedReasonLabel(reason: UploadFailureReason): string {
  switch (reason) {
    case UploadFailureReason.Duplicate:
      return 'Duplicate';
    case UploadFailureReason.UnsupportedFileFormat:
      return 'Unsupported file format';
    case UploadFailureReason.NoFileAvailable:
      return 'No file available';
    case UploadFailureReason.DownloadFailed:
      return 'Download failed';
    case UploadFailureReason.StorageError:
      return 'Storage error';
    default:
      return reason;
  }
}

/**
 * Download failed files report as CSV
 */
export function downloadFailedFilesReport(failures: UploadFailure[], uploadId: string): void {
  if (!failures || failures.length === 0) return;

  const headers = ['Filename', 'Type', 'Upload Date', 'Rejected Reason', 'Duplicate Of'];
  const rows = failures.map((failure) => [
    failure.filename,
    failure.extension?.toUpperCase() || '',
    failure.createdDate ? formatDate(failure.createdDate, true, true) : '',
    failure.rejectedReason ? getRejectedReasonLabel(failure.rejectedReason) : '',
    failure.duplicateFile?.name || '',
  ]);

  downloadCsv(headers, rows, `failed-uploads-${uploadId}.csv`);
}

/**
 * Download all skipped files report as CSV
 */
export function downloadSkippedFilesReport(skippedFiles: SkippedFile[], matterId: string): void {
  if (!skippedFiles || skippedFiles.length === 0) return;

  const headers = ['Filename', 'Type', 'Date', 'Rejected Reason', 'Duplicate Of'];
  const rows = skippedFiles.map((file) => [
    file.filename,
    file.extension?.toUpperCase() || '',
    file.createdDate ? formatDate(file.createdDate, true, true) : '',
    file.rejectedReason ? getRejectedReasonLabel(file.rejectedReason) : '',
    file.duplicateFile?.name || '',
  ]);

  downloadCsv(headers, rows, `skipped-files-${matterId}.csv`);
}

/**
 * Download all deleted files report as CSV
 */
export function downloadDeletedFilesReport(deletionHistory: DeletionHistory[], matterId: string): void {
  if (!deletionHistory || deletionHistory.length === 0) return;

  const headers = ['Filename', 'Deleted At', 'Deleted By', 'Deletion Type'];
  const rows: string[][] = [];

  deletionHistory.forEach((history) => {
    history.fileDeletion.forEach((file) => {
      rows.push([
        file.filename,
        history.createdAt ? formatDate(history.createdAt, true, true) : '',
        history.createdBy?.email || '',
        history.type === FileDeletionType.Staged ? 'Before Submission' : 'After Submission',
      ]);
    });
  });

  downloadCsv(headers, rows, `deleted-files-${matterId}.csv`);
}

/**
 * Download a single deletion batch report as CSV
 */
export function downloadDeletionBatchReport(deletion: DeletionHistory, deletionId: string): void {
  if (!deletion || !deletion.fileDeletion || deletion.fileDeletion.length === 0) return;

  const headers = ['Filename', 'Deleted At', 'Deleted By', 'Deletion Type'];
  const rows = deletion.fileDeletion.map((file) => [
    file.filename,
    deletion.createdAt ? formatDate(deletion.createdAt, true, true) : '',
    deletion.createdBy?.email || '',
    deletion.type === FileDeletionType.Staged ? 'Before Submission' : 'After Submission',
  ]);

  downloadCsv(headers, rows, `deletion-batch-${deletionId}.csv`);
}

export interface OverviewReportData {
  stats: FileHistoryStats;
  skippedFiles: SkippedFile[];
  deletedFiles: DeletionHistory[];
  awaitingFiles: File[];
  processedDocs: ProcessedDoc[];
  notProcessedDocs: NotProcessedDoc[];
}

/**
 * Download upload overview report as Excel with multiple sheets
 * Sheet 1: Overview (totals and breakdowns)
 * Sheet 2: Skipped files
 * Sheet 3: Deleted files
 * Sheet 4: Awaiting submission
 * Sheet 5: Processed docs
 * Sheet 6: Not processed docs
 */
export function downloadUploadOverviewReport(data: OverviewReportData, matterId: string): void {
  const { stats, skippedFiles, deletedFiles, awaitingFiles, processedDocs, notProcessedDocs } = data;
  const workbook = XLSX.utils.book_new();
  const totalDeleted = stats.deletionsByReason.staged + stats.deletionsByReason.submitted;

  // Sheet 1: Overview
  const overviewData = [
    ['File History Overview Report'],
    ['Generated', formatDate(new Date().toISOString(), true, true)],
    [''],
    ['Summary Statistics'],
    ['Metric', 'Count'],
    ['Files Attempted', stats.totalAttempted],
    ['Files Uploaded', stats.totalSuccessful],
    ['Files Skipped', stats.totalFailed],
    ['Files Deleted', totalDeleted],
    ['Files Awaiting Submission', stats.stagedFiles],
    ['Files Processed', stats.processed],
    ['Files Not Processed', stats.notProcessed],
    ['Upload Batches', stats.uploadCount],
    [''],
    ['Skipped Files Breakdown'],
    ['Reason', 'Count'],
    ...Object.entries(stats.failuresByReason).map(([reason, count]) => [
      getRejectedReasonLabel(reason as UploadFailureReason),
      count,
    ]),
    [''],
    ['Deleted Files Breakdown'],
    ['Type', 'Count'],
    ['Before Submission', stats.deletionsByReason.staged],
    ['After Submission', stats.deletionsByReason.submitted],
  ];

  const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
  overviewSheet['!cols'] = [{ wch: 28 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

  // Sheet 2: Skipped Files
  const skippedHeaders = ['File Name', 'Date', 'Type', 'Rejected Reason', 'Duplicate Of'];
  const skippedData = [
    skippedHeaders,
    ...skippedFiles.map((file) => [
      file.filename,
      file.createdDate ? formatDate(file.createdDate, true, true) : '',
      file.extension?.toUpperCase() || '',
      file.rejectedReason ? getRejectedReasonLabel(file.rejectedReason) : '',
      file.duplicateFile?.name || '',
    ]),
  ];

  const skippedSheet = XLSX.utils.aoa_to_sheet(skippedData);
  skippedSheet['!cols'] = [{ wch: 32 }, { wch: 20 }, { wch: 10 }, { wch: 22 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(workbook, skippedSheet, 'Skipped Files');

  // Sheet 3: Deleted Files
  const flattenedDeletions: Array<{
    filename: string;
    deletedAt: string;
    deletedBy: string;
    type: string;
  }> = [];

  deletedFiles.forEach((history) => {
    history.fileDeletion.forEach((file) => {
      flattenedDeletions.push({
        filename: file.filename,
        deletedAt: history.createdAt ? formatDate(history.createdAt, true, true) : '',
        deletedBy: history.createdBy?.email || '',
        type: history.type === FileDeletionType.Staged ? 'Before Submission' : 'After Submission',
      });
    });
  });

  const deletedHeaders = ['File Name', 'Deleted At', 'Deleted By', 'Deletion Type'];
  const deletedData = [
    deletedHeaders,
    ...flattenedDeletions.map((d) => [d.filename, d.deletedAt, d.deletedBy, d.type]),
  ];

  const deletedSheet = XLSX.utils.aoa_to_sheet(deletedData);
  deletedSheet['!cols'] = [{ wch: 32 }, { wch: 20 }, { wch: 28 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, deletedSheet, 'Deleted Files');

  // Sheet 4: Awaiting Submission
  const awaitingHeaders = ['File Name', 'Type', 'Upload Date'];
  const awaitingData = [
    awaitingHeaders,
    ...awaitingFiles.map((f) => [
      f.name,
      f.extension?.toUpperCase() || '',
      f.createdDate ? formatDate(f.createdDate, true, true) : '',
    ]),
  ];

  const awaitingSheet = XLSX.utils.aoa_to_sheet(awaitingData);
  awaitingSheet['!cols'] = [{ wch: 32 }, { wch: 10 }, { wch: 14 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, awaitingSheet, 'Awaiting Submission');

  // Sheet 5: Processed Docs
  const processedHeaders = ['File Name', 'Created Date'];
  const processedData = [
    processedHeaders,
    ...processedDocs.map((d) => [d.file?.name || '', d.createdDate ? formatDate(d.createdDate, true, true) : '']),
  ];

  const processedSheet = XLSX.utils.aoa_to_sheet(processedData);
  processedSheet['!cols'] = [{ wch: 32 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, processedSheet, 'Processed');

  // Sheet 6: Not Processed Docs
  const notProcessedHeaders = ['File Name', 'Created Date'];
  const notProcessedData = [
    notProcessedHeaders,
    ...notProcessedDocs.map((d) => [d.file?.name || '', d.createdDate ? formatDate(d.createdDate, true, true) : '']),
  ];

  const notProcessedSheet = XLSX.utils.aoa_to_sheet(notProcessedData);
  notProcessedSheet['!cols'] = [{ wch: 32 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, notProcessedSheet, 'Not Processed');

  // Download the workbook
  XLSX.writeFile(workbook, `file-history-report-${matterId}.xlsx`);
}

/**
 * Export users table data to CSV
 */
export async function exportUsersToCSV(
  users: (UserV2 & { kimActive?: boolean; runActive?: boolean })[],
  userMetadata: ReturnType<typeof useLazyUserMetadata>,
): Promise<void> {
  if (!users || users.length === 0) return;

  // Fetch metadata for all users
  const userIds = users.map((u) => u.id);
  await userMetadata.fetchMetadata(userIds);

  const headers = [
    'Email Address',
    'Role',
    'Onboarded',
    'No. of Matters',
    'Email Confirmed',
    'Kim Active',
    'Run Active',
    'Last Active',
    'Disabled',
  ];

  const rows = users.map((user) => {
    const metadata = userMetadata.getMetadata(user.id);
    let lastActiveDate = 'Never';
    if (metadata?.lastActiveAt) {
      const date = new Date(metadata.lastActiveAt * 1000);
      lastActiveDate = date.toISOString().split('T')[0];
    }

    return [
      user.email ?? '',
      user.role?.name ?? '',
      user.hasBeenActive ? 'Yes' : 'No',
      (user.matters?.length ?? 0).toString(),
      metadata?.emailConfirmed ? 'Yes' : 'No',
      user.kimActive ? 'Yes' : 'No',
      user.runActive ? 'Yes' : 'No',
      lastActiveDate,
      user.disabled ? 'Yes' : 'No',
    ];
  });

  downloadCsv(headers, rows, 'users_export.csv');
}
